import { createAsyncThunk, createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { deriveRoleKey, type RoleSignals } from '@/access/roleMapping'
import { DEFAULT_ROLE_KEY, type RoleKey } from '@/access/roles'
import { isApiError, toApiError } from '@/services'

import * as authService from '../services/authService.http'

/**
 * Session slice (PX-W6.1) — the REAL session substrate replacing the demo
 * lens as the guard's source of truth.
 *
 * The access token never lives here: it is module memory inside
 * `authService.http.ts` (AC-1 / I-1). This slice holds only what the UI
 * reads: status, the sanitised user, the derived RoleKey and the first
 * membership as `activeOrgId`.
 *
 * PX-W6.6 retired the demo lens bridge: the sign-in and boot thunks no longer
 * dispatch `roleChanged`, because the shell and every product surface now read
 * {@link selectActiveRoleKey} from THIS slice. The demo slice holds concept
 * acts only. No reducer here performs I/O (I-8) — the store-audit test
 * enforces it.
 */

/** One org membership on the wire (`MongoUser.organisations[]`). */
export interface SessionOrganisation {
  orgId: string
  orgName?: string
  role: string
}

/**
 * The `MongoUser.forApiResponse` subset the UI reads. Sensitive fields never
 * appear — the server strips them before serialising.
 */
export interface SessionUser {
  /**
   * The user's SLUG (`MongoUser.getStringId()` → JSON `id`, e.g.
   * `"user-admin"`) — NOT the canonical principal. Server-stamped author
   * fields (`MongoHoursEntry.userId`, ACL principals, message authors) carry
   * the HEX; compare those against {@link selectSessionPrincipalKey}, never
   * against this.
   */
  id: string
  /**
   * The caller's CANONICAL identity hex (`MongoUser.getIdHex()`, READ_ONLY on
   * the wire): the ObjectId the slug deterministically derives to. Present on
   * every `forApiResponse()` payload (it preserves the ObjectId), and absent
   * only on a hand-built fixture. This is the value the backend stamps into
   * author/principal fields.
   */
  idHex?: string
  name: string | null
  email: string
  emailVerified: boolean
  twoFactorEnabled: boolean
  role: string | null
  memberRole: string | null
  organisations: readonly SessionOrganisation[]
}

export type SessionStatus = 'anonymous' | 'authenticating' | 'authenticated'

/** A failed thunk's payload: only the i18n-mappable key travels to the UI. */
export interface SessionError {
  backendKey?: string
}

export interface SessionState {
  status: SessionStatus
  user: SessionUser | null
  roleKey: RoleKey | null
  /** The first membership's orgId — the active org until org-switching lands. */
  activeOrgId: string | null
  error: SessionError | null
  /**
   * Whether the boot probe (`fetchMe`) has been attempted. Flipped by the
   * probe's own `pending` reducer so `AuthGuard` fires it exactly once per
   * store without module-level flags.
   */
  bootAttempted: boolean
}

const initialState: SessionState = {
  status: 'anonymous',
  user: null,
  roleKey: null,
  activeOrgId: null,
  error: null,
  bootAttempted: false
}

/** Build the mapping's input from a session user (projects feed later waves). */
export const roleSignalsForUser = (user: SessionUser): RoleSignals => ({
  userRole: user.role ?? null,
  memberRole: user.memberRole ?? null,
  organisations: user.organisations ?? [],
  projectSide: null,
  projectRole: null
})

/** Map any thrown value to the rejection payload the reducers store. */
const rejectionKey = (error: unknown): SessionError => {
  if (isApiError(error)) return { backendKey: error.backendKey }
  return { backendKey: toApiError(error).backendKey }
}

/** Apply a signed-in user to the state (both sign-in and boot probe). */
const applyUser = (state: SessionState, user: SessionUser): void => {
  const { organisations = [] } = user
  state.status = 'authenticated'
  state.user = user
  state.roleKey = deriveRoleKey(roleSignalsForUser(user))
  state.activeOrgId = organisations[0]?.orgId ?? null
  state.error = null
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Sign in with email + password; `applyUser` derives the session role. */
export const signIn = createAsyncThunk<SessionUser, { email: string; password: string }, { rejectValue: SessionError }>(
  'session/signIn',
  async (args, { rejectWithValue }) => {
    try {
      return await authService.login(args.email, args.password)
    } catch (error) {
      return rejectWithValue(rejectionKey(error))
    }
  }
)

/** Register an account; no session change (the page chains `signIn`, D8). */
export const registerAccount = createAsyncThunk<
  SessionUser,
  { name: string; email: string; password: string },
  { rejectValue: SessionError }
>('session/registerAccount', async (args, { rejectWithValue }) => {
  try {
    return await authService.register(args.name, args.email, args.password)
  } catch (error) {
    return rejectWithValue(rejectionKey(error))
  }
})

/** End the session: server logout, token wipe, state reset. */
export const signOut = createAsyncThunk('session/signOut', async () => {
  await authService.logout()
})

/**
 * The boot probe: read `/auth/me`; the shared client silently refreshes via
 * the httpOnly cookie when one exists.
 *
 * What a NO-cookie cold boot does (deliberate, mirror-identical to
 * xelerate-ai): `/auth/me` is NOT in the transport's refresh-exclusion set,
 * so its 401 triggers the silent refresh, that refresh also 401s, and the
 * transport LATCHES a session-expired bounce to
 * `/login?reason=session-expired` — the visitor sees the front door with the
 * session-expired banner even though they never had a session. The slice side
 * of that same rejection is silent: `status` lands `anonymous` with no
 * `error` (the banner IS the UI for this outcome). Do not "fix" one side
 * without the other.
 *
 * The `condition` reads the LIVE store, so a StrictMode double-invoked effect
 * (or any concurrent mount) dispatches the probe exactly ONCE: the first
 * dispatch's `pending` synchronously flips `bootAttempted`, and every later
 * dispatch self-skips.
 */
export const fetchMe = createAsyncThunk('session/fetchMe', async () => {
  return await authService.me()
}, {
  // Exactly-once guard: see the thunk's doc comment above. `getState` is
  // untyped on a default-configured thunk, so the slice is narrowed locally
  // here rather than via a ThunkApiConfig override (overriding `state` would
  // make the AsyncThunkAction unassignable at every dispatch site).
  condition: (_, { getState }) => {
    const { session } = getState() as { session: SessionState }
    return !session.bootAttempted
  }
})

/**
 * Reload `/auth/me` for a session that already exists (TASK-7F3EE34E): an
 * accepted invite adds an organisation (and a project) to the caller, so the
 * session read at boot is stale. Unlike {@link fetchMe} it has no boot guard.
 */
export const refreshMe = createAsyncThunk('session/refreshMe', async () => {
  return await authService.me()
})

/**
 * The boot probe for a PUBLIC page that wants to know whether a session exists
 * (TASK-7F3EE34E fix wave 1, V1: `/accept-invite` opened from the e-mail in a
 * new tab). Same exactly-once guard and reducers as {@link fetchMe}, but it reads
 * the session through `authService.restoreSession`, which never triggers the
 * transport's session-expired bounce: a signed-out invitee stays on the page
 * instead of being sent to `/login?reason=session-expired`.
 */
export const probeSession = createAsyncThunk('session/probeSession', async () => {
  return await authService.restoreSession()
}, {
  condition: (_, { getState }) => {
    const { session } = getState() as { session: SessionState }
    return !session.bootAttempted && session.status === 'anonymous'
  }
})

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    /** Clear a surfaced error (e.g. when the form is edited again). */
    errorDismissed(state) {
      state.error = null
    },
    /**
     * Make `orgId` the active organisation (an accepted invite lands in the
     * inviting org; a reload restores the last choice). Ignored unless the
     * caller is a member — an unknown id must never become the active org.
     */
    activeOrgSelected(state, action: PayloadAction<string>) {
      if (state.user?.organisations.some((org) => org.orgId === action.payload)) {
        state.activeOrgId = action.payload
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.status = 'authenticating'
        state.error = null
      })
      .addCase(signIn.fulfilled, (state, action: PayloadAction<SessionUser>) => {
        applyUser(state, action.payload)
      })
      .addCase(signIn.rejected, (state, action) => {
        state.status = 'anonymous'
        state.error = action.payload ?? null
      })
      .addCase(registerAccount.pending, (state) => {
        state.error = null
      })
      .addCase(registerAccount.fulfilled, (_state) => {
        // Registration alone grants no session (D8) — nothing to apply.
      })
      .addCase(registerAccount.rejected, (state, action) => {
        state.error = action.payload ?? null
      })
      .addCase(signOut.pending, (state) => {
        state.status = 'authenticating'
      })
      .addCase(signOut.fulfilled, (state) => {
        // Preserve bootAttempted: a sign-out already knows there is no session
        // to probe for — AuthGuard must not re-probe and flash.
        state.status = 'anonymous'
        state.user = null
        state.roleKey = null
        state.activeOrgId = null
        state.error = null
        state.bootAttempted = true
      })
      .addCase(signOut.rejected, (state) => {
        // The service always clears the token; treat a failed call as signed-out.
        state.status = 'anonymous'
        state.user = null
        state.roleKey = null
        state.activeOrgId = null
        state.bootAttempted = true
      })
      .addCase(fetchMe.pending, (state) => {
        state.bootAttempted = true
        if (state.status === 'anonymous') state.status = 'authenticating'
      })
      .addCase(fetchMe.fulfilled, (state, action: PayloadAction<SessionUser>) => {
        applyUser(state, action.payload)
      })
      .addCase(refreshMe.fulfilled, (state, action: PayloadAction<SessionUser>) => {
        applyUser(state, action.payload)
      })
      .addCase(probeSession.pending, (state) => {
        state.bootAttempted = true
        state.status = 'authenticating'
      })
      .addCase(probeSession.fulfilled, (state, action: PayloadAction<SessionUser>) => {
        applyUser(state, action.payload)
      })
      .addCase(probeSession.rejected, (state) => {
        // No session: anonymous, with no error surface (the page simply stays public).
        state.status = 'anonymous'
      })
      .addCase(fetchMe.rejected, (state) => {
        // A rejected probe lands anonymous with NO error field — but it is NOT
        // silent end to end: on the no-cookie cold boot the transport has
        // already latched its `/login?reason=session-expired` bounce (see the
        // fetchMe doc comment). The slice contributes no second error surface;
        // the banner on /login is the single UI for this outcome.
        state.status = 'anonymous'
      })
  }
})

export const { errorDismissed, activeOrgSelected } = sessionSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithSession {
  session: SessionState
}

export const selectSessionStatus = (s: WithSession) => s.session.status
export const selectIsAuthenticated = (s: WithSession) => s.session.status === 'authenticated'
export const selectSessionUser = (s: WithSession) => s.session.user
export const selectRoleKey = (s: WithSession): RoleKey | null => s.session.roleKey
/**
 * The lens every surface RENDERS through — the derived session role, or the
 * ladder's default when no session role has been derived yet (a cold boot, or
 * a fixture-built store in a test).
 *
 * ⚠ Rendering only. It is NEVER the input to an access decision: `RoleGuarded`
 * reads {@link selectRoleKey} and refuses on `null`, so a surface reached
 * through the guards always has a real derived role by the time this is read.
 * PX-W6.6 pointed the 16 former `demoSlice.selectActiveRoleKey` call sites here.
 */
export const selectActiveRoleKey = (s: WithSession): RoleKey =>
  s.session.roleKey ?? DEFAULT_ROLE_KEY
/**
 * The caller's PRINCIPAL KEY — the single value to compare a server-stamped
 * author/owner field against (`hoursEntry.userId`, ACL principal entries,
 * message `userId`). Prefers the canonical `idHex` and falls back to `id`
 * only for an ObjectId-id user (where the two are equal by construction) or a
 * fixture that carries no hex.
 *
 * Derived ONCE here so no surface repeats the trap that shipped in W6.4's
 * first cut: comparing a hex `userId` against the SLUG `user.id` matches
 * nothing, so a list of the caller's own rows renders permanently empty while
 * the rows exist on the server.
 */
export const selectSessionPrincipalKey = (s: WithSession): string | null =>
  s.session.user?.idHex ?? s.session.user?.id ?? null
export const selectActiveOrgId = (s: WithSession): string | null => s.session.activeOrgId
export const selectSessionError = (s: WithSession) => s.session.error
export const selectBootAttempted = (s: WithSession) => s.session.bootAttempted

/**
 * The caller's role in the ACTIVE organisation (`owner` | `admin` | `member` |
 * `viewer`), or null before a session. Drives UI that depends on the org role
 * (the owner option on the invite form, the project invite gate); the server
 * still decides — this only hides what it would refuse.
 */
export const selectActiveOrgRole = createSelector(
  [selectSessionUser, selectActiveOrgId],
  (user, activeOrgId): string | null =>
    user?.organisations.find((org) => org.orgId === activeOrgId)?.role.toLowerCase() ?? null
)
